import "~/styles/globals.css"

import { type ComponentType, StrictMode } from "react"
import { createRoot } from "react-dom/client"
import {
	createBrowserRouter,
	Navigate,
	type RouteObject,
	RouterProvider
} from "react-router-dom"
import { GodCatchAllPage } from "./routes/god/catch-all"
import { ConsentPage } from "./routes/consent"
import { LoginPage } from "./routes/login"
import { useTeam } from "./hooks/useTeam"
import { LoadingState } from "./components/screens/StateScreen"
import AutomationEditorPage from "./routes/main/automation-editor"
import AutomationRunsPage from "./routes/main/automation-runs"
import AutomationsPage from "./routes/main/automations"
import InboxPage from "./routes/main/inbox"
import OnboardingPage from "./routes/main/onboarding"
import IssueDetailsPage from "./routes/main/issue-details/page"
import KnowledgeBaseRoute from "./routes/main/knowledge-base"
import KnowledgeBaseDocumentRoute from "./routes/main/knowledge-base-document"
import MyIssuesPage from "./routes/main/my-issues"
import ProjectsPage from "./routes/main/projects"
import ProjectDetailsPage from "./routes/main/project-details"
import FormsPage from "./routes/main/forms"
import FormPage from "./routes/main/form"
import FormSubmissionsPage from "./routes/main/form-submissions"
import FormSettingsPage from "./routes/main/form-settings"
import TeamPage from "./routes/main/team"
import TeamQueuePage from "./routes/main/team-queue"
import TeamSuggestionDetailsPage from "./routes/main/team-suggestion-details"
import TeamSuggestionsPage from "./routes/main/team-suggestions"
import NativeAuthPage from "./routes/native-auth"
import NotFoundPage from "./routes/not-found"
import OrgEntryPage from "./routes/org/entry"
import { OrgIndexRedirectPage } from "./routes/org/index-redirect"
import { ErrorBoundaryPage } from "./routes/root/error"
import { RootLayout } from "./routes/root/layout"
import SharePage from "./routes/share"
import FormSharePage from "./routes/share-form"
import {
	LandingOrCustomSuggestions,
	NewOrCustomSuggestion
} from "./routes/suggestions/custom-domain"
import {
	PublicSuggestionDetailExternalRoute,
	PublicSuggestionIndexExternalRoute,
	PublicSuggestionNewExternalRoute,
	PublicSuggestionsExternalRoute
} from "./routes/suggestions/native-external"
import UnauthorizedPage from "./routes/unauthorized"

const lazyFromDefault =
	(loader: () => Promise<{ default: ComponentType }>) => async () => {
		const module = await loader()
		return { Component: module.default }
	}

const routeConfig: RouteObject[] = [
	{
		Component: RootLayout,
		errorElement: <ErrorBoundaryPage />,
		children: [
			{
				path: "/__internal/design",
				lazy: lazyFromDefault(() => import("./routes/internal/design"))
			},
			{
				path: "/",
				Component: LandingOrCustomSuggestions
			},
			{
				path: "/login",
				Component: LoginPage
			},
			{
				path: "/consent",
				Component: ConsentPage
			},
			{
				path: "/native-auth",
				Component: NativeAuthPage
			},
			{
				path: "/auth-proxy",
				Component: NativeAuthPage
			},
			{
				path: "/invite",
				lazy: lazyFromDefault(() => import("./routes/invite/fallback"))
			},
			{
				path: "/invite/:inviteId",
				lazy: lazyFromDefault(() => import("./routes/invite/page"))
			},
			{
				path: "/new",
				Component: NewOrCustomSuggestion
			},
			{
				path: "/s",
				Component: PublicSuggestionIndexExternalRoute
			},
			{
				path: "/s/:orgSlug/:teamSlug",
				Component: PublicSuggestionsExternalRoute
			},
			{
				path: "/s/:orgSlug/:teamSlug/new",
				Component: PublicSuggestionNewExternalRoute
			},
			{
				path: "/s/:orgSlug/:teamSlug/:suggestionFriendlyId",
				Component: PublicSuggestionDetailExternalRoute
			},
			{
				path: "/unauthorized",
				Component: UnauthorizedPage
			},
			{
				path: "/share/:shareId",
				Component: SharePage
			},
			{
				path: "/share/form/:shareId",
				Component: FormSharePage
			},
			{
				path: "/f/:sharePath",
				Component: FormSharePage
			},
			{
				path: "/god",
				lazy: lazyFromDefault(() => import("./routes/god/layout")),
				children: [
					{
						index: true,
						lazy: lazyFromDefault(() => import("./routes/god/overview"))
					},
					{
						path: "users",
						lazy: lazyFromDefault(() => import("./routes/god/users"))
					},
					{
						path: "users/:id",
						lazy: lazyFromDefault(() => import("./routes/god/user"))
					},
					{
						path: "organizations",
						lazy: lazyFromDefault(() => import("./routes/god/organizations"))
					},
					{
						path: "organizations/:id",
						lazy: lazyFromDefault(() => import("./routes/god/organization"))
					},
					{
						path: "organizations/:id/teams/:teamId",
						lazy: lazyFromDefault(
							() => import("./routes/god/organization-team")
						)
					},
					{
						path: "notification-failures",
						lazy: lazyFromDefault(
							() => import("./routes/god/notification-failures")
						)
					},
					{
						path: "live-updates",
						lazy: lazyFromDefault(() => import("./routes/god/live-updates"))
					},
					{
						path: "force-delete",
						lazy: lazyFromDefault(() => import("./routes/god/force-delete"))
					},
					{
						path: "audit-logs",
						lazy: lazyFromDefault(() => import("./routes/god/audit-logs"))
					},
					{
						path: "buape/project",
						lazy: lazyFromDefault(() => import("./routes/god/projects"))
					},
					{
						path: "*",
						Component: GodCatchAllPage
					}
				]
			},
			{
				path: "/:orgSlug",
				Component: OrgEntryPage,
				children: [
					{
						index: true,
						Component: OrgIndexRedirectPage
					},
					{
						path: "me/inbox",
						Component: InboxPage
					},
					{
						path: "me/settings",
						lazy: lazyFromDefault(() => import("./routes/main/user-settings"))
					},
					{
						path: "me/onboarding",
						Component: OnboardingPage
					},
					{
						path: "me/issues",
						Component: MyIssuesPage
					},
					{
						path: "settings",
						lazy: lazyFromDefault(() => import("./routes/main/org-settings"))
					},
					{
						path: ":teamSlug",
						children: [
							{
								index: true,
								Component: function TeamIndexRedirect() {
									const { team, orgSlug, isLoading } = useTeam()
									if (isLoading) return <LoadingState />
									return (
										<Navigate
											replace
											to={
												team?.enableHome
													? `/${orgSlug}/${team.slug}/home`
													: team?.enableIssues
														? `/${orgSlug}/${team.slug}/issues`
														: `/${orgSlug}/me/issues`
											}
										/>
									)
								}
							},
							{
								path: "issues",
								Component: TeamPage
							},
							{
								path: "home",
								lazy: lazyFromDefault(() => import("./routes/main/team-home"))
							},
							{
								path: "queue",
								Component: TeamQueuePage
							},
							{
								path: "settings",
								lazy: lazyFromDefault(
									() => import("./routes/main/team-settings")
								)
							},
							{
								path: "projects",
								Component: ProjectsPage
							},
							{
								path: "projects/new",
								Component: ProjectDetailsPage
							},
							{
								path: "projects/:projectId",
								Component: ProjectDetailsPage
							},
							{
								path: "forms",
								Component: FormsPage
							},
							{
								path: "forms/:formId",
								Component: FormPage
							},
							{
								path: "forms/:formId/edit",
								Component: FormPage
							},
							{
								path: "forms/:formId/settings",
								Component: FormSettingsPage
							},
							{
								path: "forms/:formId/submissions",
								Component: FormSubmissionsPage
							},
							{
								path: "automations",
								Component: AutomationsPage
							},
							{
								path: "automations/new",
								Component: AutomationEditorPage
							},
							{
								path: "automations/:automationId/runs",
								Component: AutomationRunsPage
							},
							{
								path: "automations/:automationId",
								Component: AutomationEditorPage
							},
							{
								path: "suggestions",
								Component: TeamSuggestionsPage
							},
							{
								path: "suggestions/:suggestionId",
								Component: TeamSuggestionDetailsPage
							},
							{
								path: "kb",
								Component: KnowledgeBaseRoute
							},
							{
								path: "kb/:documentId",
								Component: KnowledgeBaseDocumentRoute
							},
							{
								path: ":issueFriendlyId",
								Component: IssueDetailsPage
							}
						]
					}
				]
			},
			{
				path: "*",
				Component: NotFoundPage
			}
		]
	}
]

const root = document.getElementById("root")

if (!root) {
	throw new Error("Root element not found")
}

window.addEventListener("vite:preloadError", () => {
	if (!navigator.onLine) return

	try {
		// Keep this across reloads: a persistently missing chunk must not loop.
		const key = "orbit:preload-reload"
		if (sessionStorage.getItem(key) === import.meta.url) return
		sessionStorage.setItem(key, import.meta.url)
	} catch {
		// Without a durable guard, leave recovery to the normal error boundary.
		return
	}

	// Use a normal reload so existing beforeunload draft protection still runs.
	window.location.reload()
})

const router = createBrowserRouter(routeConfig)

createRoot(root).render(
	<StrictMode>
		<RouterProvider router={router} />
	</StrictMode>
)
